/**
 * Esoteria Music Player — plays the original engine's (level, intensity) song
 * rows through WebAudio.
 *
 * The model is the binary's, not ours (see `music-states.ts` for provenance):
 *
 *   - A level resolves to a song through one of two level->song tables, picked
 *     at random once per playthrough.
 *   - A song has three intensity rows; each row is an *ordered* sequence of
 *     music container entries.
 *   - Each entry is a kind-1 "intro" whose kind-3 "continuation" sits at
 *     `id + 1`. The original plays the two gaplessly as one unit
 *     (`0x4d45a0(t, n, n + 1, -1, 0)`), so this player schedules them as a pair.
 *
 * The player therefore walks the row in order, pair by pair, wrapping at the
 * end. There is no random draw and no weighting — that was a fiction of the
 * previous implementation.
 *
 * Scheduling is lookahead-based: a JS interval checks whether the next pair
 * needs queueing and, if so, calls `source.start(nextTime)` with an exact
 * AudioContext timestamp so playback is sample-accurate and gapless.
 */

import { assetUrl } from './asset-base'
import {
  DEFAULT_INTENSITY,
  DEFAULT_LEVEL,
  INTENSITY_LABELS,
  MUSIC_INTENSITIES,
  clampIntensity,
  isMusicLevelId,
  pairEntryIds,
  pickLevelSongTable,
  segmentsFor,
  songForLevel,
  type LevelSongTable,
  type MusicIntensity,
  type MusicLevelId,
  type MusicSegmentPair,
  type MusicSongId,
} from './music-states'

/**
 * A single music container entry and its decoded buffer.
 *
 * Note the absence of a `weight`: the engine does not weight these. A row is
 * an ordered sequence, so there is nothing to bias.
 */
export interface SegmentInfo {
  id: number
  buffer?: AudioBuffer
}

/** Everything that identifies what the player is currently playing. */
export interface MusicPlayerState {
  level: MusicLevelId
  intensity: MusicIntensity
  /** `null` when the level does not resolve to a song. */
  songId: MusicSongId | null
  table: LevelSongTable
  /**
   * Short display string for HUD/debug, e.g. `L3 S2 CALM`. The intensity word
   * is INFERRED — see `INTENSITY_LABELS`.
   */
  label: string
}

export interface MusicPlayerOptions {
  /** Starting level index, 0..9. */
  level?: MusicLevelId
  /** Starting intensity row, 0..2. */
  intensity?: MusicIntensity
  /**
   * Which level->song table to use. Omit to have one picked at random, the
   * way the original picks once per playthrough.
   */
  table?: LevelSongTable
}

/**
 * MusicPlayer — low-level AudioContext driver for the (level, intensity) model.
 *
 * See `wiki/music-system-reverse-engineering.md` for the binary analysis this
 * mirrors.
 */
export class MusicPlayer {
  /** Current level index driving song selection. */
  private level: MusicLevelId = DEFAULT_LEVEL

  /** Current intensity row within the selected song. */
  private intensity: MusicIntensity = DEFAULT_INTENSITY

  /** Which of the two level->song tables this playthrough uses. */
  private table: LevelSongTable

  /** Song resolved from (level, table); null when the level is unmapped. */
  private songId: MusicSongId | null = null

  /** Ordered intro/continuation pairs for the current (song, intensity). */
  private pairs: MusicSegmentPair[] = []

  /** Cursor into `pairs`. Wraps at the end of the row. */
  private cursor = 0

  /** Audio context for playback (created on first start) */
  private audioCtx: AudioContext | null = null

  /** Registry of every entry id we have touched, with its decoded buffer. */
  private segments: Map<number, SegmentInfo> = new Map()

  /** Lookahead window in seconds for proactive scheduling */
  private lookAhead = 0.1 // 100ms

  /** Polling interval for lookahead checks in ms */
  private scheduleIntervalMs = 25

  /** Active AudioBufferSourceNodes for cleanup */
  private activeSources: Set<AudioBufferSourceNode> = new Set()

  /** Gain node for volume control */
  private gainNode: GainNode | null = null

  /** Scheduled next playback time in seconds (AudioContext time) */
  private nextTime = 0

  /** Interval ID for the scheduling loop */
  private scheduleIntervalId: ReturnType<typeof setInterval> | null = null

  /** Volume gain value (0.0 to 1.0) */
  private volume = 0.5

  constructor(options: MusicPlayerOptions = {}) {
    this.table = options.table ?? pickLevelSongTable()

    if (options.level !== undefined) {
      if (isMusicLevelId(options.level)) {
        this.level = options.level
      } else {
        console.warn(`Ignoring out-of-range initial level ${options.level}`)
      }
    }

    if (options.intensity !== undefined) {
      this.intensity = clampIntensity(options.intensity)
    }

    this._resolveRow()
  }

  /**
   * Re-resolve (level, table) -> song and (song, intensity) -> pair sequence,
   * then restart the row from its first pair.
   */
  private _resolveRow(): void {
    this.songId = songForLevel(this.level, this.table)
    this.pairs = this.songId === null ? [] : segmentsFor(this.songId, this.intensity)
    this.cursor = 0
  }

  /** Get (creating on demand) the registry record for a container entry id. */
  private _segment(id: number): SegmentInfo {
    let seg = this.segments.get(id)
    if (!seg) {
      seg = { id }
      this.segments.set(id, seg)
    }
    return seg
  }

  /** Every segment record needed for one intensity row of the current song. */
  private _segmentsForIntensity(intensity: MusicIntensity): SegmentInfo[] {
    if (this.songId === null) return []
    return pairEntryIds(segmentsFor(this.songId, intensity)).map((id) => this._segment(id))
  }

  /**
   * Start the music engine. Must be called after a user gesture
   * to satisfy browser autoplay policies.
   */
  async start(): Promise<void> {
    // Create AudioContext
    this.audioCtx = new (window.AudioContext || (window as any).webkitAudioContext)()

    if (this.audioCtx.state === 'suspended') {
      await this.audioCtx.resume()
    }

    // Create gain node for volume control
    this.gainNode = this.audioCtx.createGain()
    this.gainNode.gain.value = this.volume
    this.gainNode.connect(this.audioCtx.destination)

    // Pre-decode the row we are about to play so the polling loop always finds
    // a decoded buffer to schedule. Without this, the first few lookahead ticks
    // would race the async decode and never start playback.
    await this._decodeSegments(this._segmentsForIntensity(this.intensity))

    // Warm the other two intensity rows of the same song in the background so
    // an intensity change mid-play does not stall. Fire-and-forget: the
    // scheduler already tolerates an undecoded segment by retrying.
    for (const other of MUSIC_INTENSITIES) {
      if (other === this.intensity) continue
      void this._decodeSegments(this._segmentsForIntensity(other))
    }

    // Schedule first pair
    this._scheduleNextPair()

    // Start the JS polling loop for lookahead checks
    this.scheduleIntervalId = setInterval(() => this._checkAndSchedule(), this.scheduleIntervalMs)

    console.log(`Music player started. ${this._label()}`)
    console.log(`Lookahead: ${this.lookAhead}s, Schedule interval: ${this.scheduleIntervalMs}ms`)
  }

  /**
   * Check lookahead and schedule the next pair if needed.
   * Called by the polling interval.
   */
  private _checkAndSchedule(): void {
    if (!this.audioCtx) return
    if (this.audioCtx.state === 'suspended') {
      this.audioCtx.resume().catch(() => {})
      return
    }

    // If we're within the lookahead window, schedule the next pair
    if (this.audioCtx.currentTime + this.lookAhead >= this.nextTime) {
      this._scheduleNextPair()
    }
  }

  /**
   * Schedule the next intro/continuation pair from the current row.
   *
   * Decoding is fire-and-forget: if the pair's intro has no decoded buffer
   * yet, we trigger a decode and return without advancing the cursor. The
   * polling loop will retry on the next tick once the decode resolves, so the
   * row still plays in order.
   */
  private _scheduleNextPair(): void {
    if (!this.audioCtx || !this.gainNode) return

    if (this.pairs.length === 0) {
      console.warn(
        `No music entries for level ${this.level} (table "${this.table}", song ` +
          `${this.songId ?? 'none'}) intensity ${this.intensity}`,
      )
      return
    }

    const pair = this.pairs[this.cursor % this.pairs.length]
    const intro = this._segment(pair.introId)
    const continuation = this._segment(pair.continuationId)

    if (!intro.buffer) {
      // Kick off the decode for this pair. Resolution is handled out-of-band
      // by the polling loop in _checkAndSchedule; the cursor does not advance,
      // so grid order is preserved.
      void this._decodeSegments([intro, continuation])
      console.warn(`Music entry ${intro.id} not yet decoded; will retry next tick`)
      return
    }

    this._scheduleBuffer(intro.buffer)

    if (continuation.buffer) {
      // The continuation half starts exactly where the intro ends — that is
      // the whole point of the kind-1/kind-3 split.
      this._scheduleBuffer(continuation.buffer)
    } else {
      // Play the intro alone this time around and warm the continuation for
      // the next pass rather than stalling the row.
      void this._decodeSegments([continuation])
    }

    this.cursor = (this.cursor + 1) % this.pairs.length

    // Cap nextTime to prevent runaway (limit to 300s from now)
    const maxTime = this.audioCtx.currentTime + 300
    if (this.nextTime > maxTime) {
      this.nextTime = maxTime
    }
  }

  /** Create a one-shot source for `buffer` at `nextTime` and advance the clock. */
  private _scheduleBuffer(buffer: AudioBuffer): void {
    if (!this.audioCtx || !this.gainNode) return

    // Create a fresh source node (single-use)
    const source = this.audioCtx.createBufferSource()
    source.buffer = buffer

    // One-shot: each segment plays exactly once. The lookahead scheduler
    // (_checkAndSchedule) queues the FOLLOWING pair to start at nextTime, so
    // playback is gapless without looping. Setting loop=true here was a bug: it
    // made every scheduled segment repeat forever while the scheduler kept
    // stacking new looping sources on top every ~lookAhead seconds, producing an
    // ever-growing pile of overlapping short stingers (heard as looping
    // explosion/water noise) and saturating the audio thread (frame drops).
    source.loop = false

    // Connect through gain node for volume control
    source.connect(this.gainNode)

    // Track for cleanup, and self-evict once the one-shot finishes so
    // activeSources cannot grow without bound.
    this.activeSources.add(source)
    source.onended = () => {
      try {
        source.disconnect()
      } catch {
        // already disconnected — ignore
      }
      this.activeSources.delete(source)
    }

    // Schedule to start at precisely nextTime
    try {
      source.start(this.nextTime)
    } catch (e) {
      // Source may have already started/stopped - ignore
      console.debug('Source start call ignored (likely already playing):', (e as Error).message)
    }

    // Advance nextTime by the buffer duration (for lookahead tracking)
    this.nextTime += buffer.duration
  }

  /**
   * Fetch and decode music container entries, caching the AudioBuffers.
   *
   * @param segments - Segments to attempt decoding
   */
  private async _decodeSegments(segments: SegmentInfo[]): Promise<void> {
    const audioCtx = this.audioCtx
    if (!audioCtx) return

    for (const seg of segments) {
      // Skip if already decoded
      if (seg.buffer) continue

      // Try to fetch and decode the Opus file (from R2 in prod, /public in dev)
      const opusPath = assetUrl(`audio/music/seg_${String(seg.id).padStart(4, '0')}.opus`)

      try {
        const response = await fetch(opusPath)
        if (!response.ok) throw new Error(`HTTP ${response.status}`)

        const arrayBuffer = await response.arrayBuffer()

        // Decode on the playback context. A row is up to 20 files and three
        // rows get warmed, so spinning up a throwaway AudioContext per file
        // (as an earlier revision did) blows straight through the browser's
        // per-page context limit.
        const buffer = await audioCtx.decodeAudioData(arrayBuffer)

        // Cache the decoded buffer
        seg.buffer = buffer
        this.segments.set(seg.id, seg)

        console.log(`Decoded music entry ${seg.id} from ${opusPath}, duration: ${buffer.duration}s`)
      } catch (err) {
        console.warn(`Failed to decode music entry ${seg.id} from ${opusPath}:`, err)
        // Could not fetch/decode - continue with existing cache
      }
    }
  }

  /**
   * Stop the music engine and clean up all resources.
   */
  stop(): void {
    if (this.scheduleIntervalId !== null) {
      clearInterval(this.scheduleIntervalId)
      this.scheduleIntervalId = null
    }

    // Stop and disconnect all active sources (one-shots may still be mid-play).
    for (const source of this.activeSources) {
      try {
        source.onended = null
        source.stop()
        source.disconnect()
      } catch {
        // Already stopped - ignore
      }
    }
    this.activeSources.clear()

    // Dispose gain node
    if (this.gainNode) {
      try {
        this.gainNode.disconnect()
      } catch {
        // Already disconnected - ignore
      }
      this.gainNode = null
    }

    // Suspend audio context
    if (this.audioCtx) {
      this.audioCtx.suspend().catch(() => {})
      this.audioCtx = null
    }

    console.log('Music player stopped')
  }

  /**
   * Move to a different level. Re-resolves the song through the playthrough's
   * level->song table and restarts the row from its first pair.
   */
  setLevel(level: MusicLevelId): void {
    if (!isMusicLevelId(level)) {
      console.warn(`Unknown music level: ${level}`)
      return
    }
    if (level === this.level) return

    const previous = this.level
    this.level = level
    this._resolveRow()
    void this._decodeSegments(this._segmentsForIntensity(this.intensity))
    console.log(`Music level ${previous} -> ${level} (${this._label()})`)
  }

  /**
   * Change the intensity row within the current song. Takes effect on the next
   * scheduling tick; the already-queued pair finishes first.
   */
  setIntensity(intensity: MusicIntensity): void {
    const clamped = clampIntensity(intensity)
    if (clamped !== intensity) {
      console.warn(`Music intensity ${intensity} out of range; clamped to ${clamped}`)
    }
    if (clamped === this.intensity) return

    const previous = this.intensity
    this.intensity = clamped
    this._resolveRow()
    void this._decodeSegments(this._segmentsForIntensity(this.intensity))
    console.log(
      `Music intensity ${previous} (${INTENSITY_LABELS[previous]}) -> ` +
        `${clamped} (${INTENSITY_LABELS[clamped]})`,
    )
  }

  /**
   * Switch which level->song table is in force. The original picks once per
   * playthrough, so this exists mainly for tests and the debug UI.
   */
  setSongTable(table: LevelSongTable): void {
    if (table !== 'a' && table !== 'b') {
      console.warn(`Unknown level->song table: ${table}`)
      return
    }
    if (table === this.table) return
    this.table = table
    this._resolveRow()
  }

  /**
   * Jump to an intensity and restart the row immediately from its first pair,
   * for explicit gameplay triggers (the original's "force state" globals at
   * `0x5845dc`/`0x5845e0`).
   */
  forceIntensity(intensity: MusicIntensity): void {
    const clamped = clampIntensity(intensity)
    this.intensity = clamped
    this._resolveRow()
    void this._decodeSegments(this._segmentsForIntensity(this.intensity))
  }

  /**
   * Set volume for the music player (0.0 to 1.0).
   */
  setVolume(gain: number): void {
    const clamped = Math.max(0, Math.min(1, gain))
    this.volume = clamped

    if (this.gainNode) {
      this.gainNode.gain.value = clamped
    }
  }

  /** Everything identifying what is playing right now. */
  getState(): MusicPlayerState {
    return {
      level: this.level,
      intensity: this.intensity,
      songId: this.songId,
      table: this.table,
      label: this._label(),
    }
  }

  getLevel(): MusicLevelId {
    return this.level
  }

  getIntensity(): MusicIntensity {
    return this.intensity
  }

  getSongId(): MusicSongId | null {
    return this.songId
  }

  getSongTable(): LevelSongTable {
    return this.table
  }

  /** The ordered pairs currently queued for playback, in grid order. */
  getSegmentPairs(): readonly MusicSegmentPair[] {
    return this.pairs
  }

  private _label(): string {
    const song = this.songId === null ? '-' : String(this.songId)
    return `L${this.level} S${song} ${INTENSITY_LABELS[this.intensity].toUpperCase()}`
  }

  /**
   * Get the current approximate playback time.
   * @returns Time in seconds since playback started
   */
  getCurrentTime(): number {
    if (!this.audioCtx) return 0
    return this.nextTime - 10 // approximate offset
  }

  /**
   * Drop a cached buffer and re-fetch it. Useful for testing or when assets
   * are updated.
   * @param entryId - The music container entry id to reload
   */
  async reloadSegment(entryId: number): Promise<boolean> {
    const inRow = pairEntryIds(this.pairs).includes(entryId)
    if (!inRow) {
      console.warn(`Music entry ${entryId} is not part of the current row (${this._label()})`)
      return false
    }

    const seg = this._segment(entryId)
    seg.buffer = undefined

    await this._decodeSegments([seg])
    return !!seg.buffer
  }

  /**
   * Check if music player is running (audio context active and scheduling).
   */
  get isRunning(): boolean {
    return this.audioCtx?.state === 'running' && this.scheduleIntervalId !== null
  }
}
